import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";
import { AiOutlineLoading } from "react-icons/ai";
import { useTranslation } from "react-i18next";
import useAuth from "../../../../hooks/useAuth";
import { usePermissions } from "../../../../utils/permissions";
import { Cap, Level } from "../../../../utils/rbacCatalog";
import { USER_LIST_COMPONENT_TYPE } from "../../../../constants/types";
import Pagination from "../../../public/Pagination";
import { CustomButton } from "../../../atoms/CustomButton";
import { useGetRoleList, useGetTypeList } from "../../../../hooks/users";
import { UsersCardView } from "./UsersCardView";
import { Dropdown } from "flowbite-react";
import SecureStorage from "../../../../utils/secureStorage";
import NoTranslate from "../../../atoms/NoTranslate";

export type UserMatch = {
  field: string;
  path?: string;
  matched_in: string;
  value?: any;
  term?: string;
};

export type User = {
  id: number;
  user_identification: string;
  user_user_name: string | null;
  user_last_name: string | null;
  user_first_name: string | null;
  user_birth_date: string | null;
  user_avatar: string | null;
  user_phone: string | null;
  user_email: string;
  user_country_code: string | null;
  user_is_active: boolean;
  user_role: string;
  user_type: string;
  user_creation_date_time: string;
  user_account_status: string;
  user_last_connection: string | null;
  matches?: UserMatch[];
};

type UsersListComponentProps = {
  users: Array<User>;
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
  refetch: () => void;
  limit?: number;
  selectedRowIds: Set<string>;
  setSelectedRowIds: Dispatch<SetStateAction<Set<string>>>;
  type?: USER_LIST_COMPONENT_TYPE;
  pagination?: any;
  offset: number;
  setOffset: (value: number) => void;
  setLimit?: Dispatch<SetStateAction<12 | 30 | 50>>;
  sortOrder?: "ASC" | "DESC" | "";
  setSortOrder?: (value: "ASC" | "DESC" | "") => void;
  singleColumn?: boolean;
};
const UsersListComponent = ({
  users,
  isLoading,
  isError,
  isSuccess,
  refetch,
  limit = 50,
  selectedRowIds,
  setSelectedRowIds,
  type = USER_LIST_COMPONENT_TYPE.USERS,
  pagination,
  offset = 0,
  setOffset,
  setLimit,
  sortOrder,
  setSortOrder,
  singleColumn = false,
}: UsersListComponentProps) => {
  const [t] = useTranslation();
  const [selectAll, setSelectAll] = useState<{
    isSelected: boolean;
    selectPage: number;
  }>({
    isSelected: false,
    selectPage: 0,
  });

  const { user } = useAuth();
  const permissions = usePermissions(user);
  const canReadRole = permissions.can(Cap.USERS_USER_ROLE, Level.VIEW);
  const canReadType = permissions.can(Cap.USERS_USER_TYPE, Level.VIEW);
  const canSeeProfile = permissions.can(Cap.USERS_DETAILS, Level.VIEW);

  const toggleRowSelection = (rowId: string) => {
    setSelectedRowIds((prev) => {
      const newSelected = new Set(prev);
      if (newSelected.has(rowId)) {
        newSelected.delete(rowId);
      } else {
        newSelected.add(rowId);
      }
      return newSelected;
    });
  };

  const {
    data: resRole,
    isLoading: isLoadingRole,
    isError: isErrorRole,
    isSuccess: isSuccessRole,
    refetch: refetchRole,
  } = useGetRoleList({
    user_identification: user.user_identification,
    entity_identification: user.entity_identification,
    enabled: canReadRole,
  });

  const {
    data: resType,
    isLoading: isLoadingType,
    isError: isErrorType,
    isSuccess: isSuccessType,
    refetch: refetchType,
  } = useGetTypeList({
    user_identification: user.user_identification,
    entity_identification: user.entity_identification,
    enabled: canReadType,
  });

  const calculateCurrentPage = useCallback(() => {
    const totalItems = pagination?.total ?? 0;
    if (limit <= 0) {
      throw new Error("Limit must be a positive integer.");
    }

    if (offset < 0) {
      throw new Error("Offset cannot be negative.");
    }

    if (totalItems === 0) {
      return 0;
    }

    let newOffset = Math.min(offset, Math.max(0, totalItems - 1));

    const currentPage = Math.floor(newOffset / limit);

    return currentPage;
  }, [offset, pagination?.total, limit]);

  const handlePageChange = (pageNumber: number) => {
    setOffset(pageNumber * limit);
  };

  useEffect(() => {
    if (selectedRowIds?.size == 0 && selectAll.isSelected) {
      setSelectAll({ isSelected: false, selectPage: 0 });
    }
  }, [selectedRowIds]);

  if (isLoading) {
    return (
      <div className="min-h-[340px] flex items-center justify-center">
        <AiOutlineLoading className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-[340px] flex items-center justify-center">
        <CustomButton
          variant="primary"
          onClick={() => refetch()}
          title="Reload"
        />
      </div>
    );
  }

  const handleSelectAll = (checked: boolean) => {
    setSelectAll({
      isSelected: checked,
      selectPage: checked ? calculateCurrentPage() || 0 : 0,
    });

    setSelectedRowIds(
      checked
        ? new Set((users ?? []).map((item: any) => item?.user_identification))
        : new Set()
    );
  };

  return (
    <div className="min-h-[340px]">
      {isSuccess && (
        <UsersCardView
          users={users || []}
          selectedRowIds={selectedRowIds}
          toggleRowSelection={toggleRowSelection}
          selectAll={
            selectAll.isSelected &&
            selectAll.selectPage === (calculateCurrentPage() || 0)
          }
          onSelectAll={handleSelectAll}
          type={type}
          resRole={resRole}
          resType={resType}
          isLoadingRole={isLoadingRole}
          isLoadingType={isLoadingType}
          isErrorRole={isErrorRole}
          isErrorType={isErrorType}
          isSuccessRole={isSuccessRole}
          isSuccessType={isSuccessType}
          refetchRole={refetchRole}
          refetchType={refetchType}
          canReadRole={canReadRole}
          canReadType={canReadType}
          sortOrder={sortOrder}
          setSortOrder={setSortOrder}
          canSeeProfile={canSeeProfile}
          singleColumn={singleColumn}
        />
      )}

      {pagination?.total > 0 && isSuccess && (
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 my-6 px-2">
          <div className="text-sm text-gray-600 text-center sm:text-left">
            <span className="font-normal">
              {t("dashboard.usersList.key2")}{" "}
            </span>
            <span className="font-semibold">
              <NoTranslate>{offset + 1}</NoTranslate>
            </span>
            <span className="font-normal">
              {" "}
              {t("dashboard.usersList.key3")}{" "}
            </span>
            <span className="font-semibold">
              <NoTranslate>
                {users?.length ? users?.length + offset : 0}
              </NoTranslate>
            </span>
            <span className="font-normal">
              {" "}
              {t("dashboard.usersList.key4")}{" "}
            </span>
            <span className="font-semibold">
              <NoTranslate>{pagination?.total ?? 0}</NoTranslate>
            </span>
            <span className="font-normal">
              {" "}
              {t("dashboard.usersList.key5")}
            </span>
          </div>

          <div className="flex items-center gap-2">
            {setLimit && (
              <Dropdown
                label=""
                dismissOnClick
                renderTrigger={() => (
                  <CustomButton
                    variant="secondary"
                    size="sm"
                    title={`${limit}`}
                    icon="chevronDown"
                    className="min-w-[44px]"
                  />
                )}
              >
                {[12, 30, 50].map((item: any) => (
                  <Dropdown.Item
                    key={item}
                    onClick={() => {
                      SecureStorage.setUsersListLimit(item);
                      setLimit(item);
                      setOffset(0);
                    }}
                  >
                    <NoTranslate>{item}</NoTranslate>
                  </Dropdown.Item>
                ))}
              </Dropdown>
            )}

            <Pagination
              pageCount={pagination?.total_pages}
              currentPage={calculateCurrentPage()}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersListComponent;
